import { ref, watch, computed } from 'vue';
import { api } from '@/services/api';

/**
 * Documentation Form Management Composable
 *
 * Handles form state, validation, field updates, and save operations
 * for equipment documentation forms.
 *
 * @param {Object} options
 * @param {number} options.equipmentId - Equipment ID
 * @param {string} options.equipmentType - Equipment type/category
 * @param {number} options.siteId - Site ID
 * @param {Function} options.onSaveSuccess - Callback on successful save
 * @param {Function} options.onSaveError - Callback on save error
 * @param {Function} options.notifySuccess - Quasar notify success function
 * @param {Function} options.notifyError - Quasar notify error function
 * @param {boolean} options.autoSave - Enable auto-save on changes
 * @param {number} options.autoSaveDelay - Auto-save delay in ms (default: 2000)
 */
export function useDocumentationForm({
  equipmentId,
  equipmentType,
  siteId,
  onSaveSuccess,
  onSaveError,
  notifySuccess,
  notifyError,
  autoSave = false,
  autoSaveDelay = 2000,
}) {
  // Form state
  const documentation = ref({
    equipment_id: null,
    equipment_type: null,
    airflow_data: {
      supply_flow: null,
      return_flow: null,
      outdoor_air_flow: null,
      static_pressure: null,
      zones_served: [],
      connected_equipment: [],
    },
    function_description: {
      purpose: '',
      operation_modes: '',
      control_strategy: '',
    },
    program_schedule: {
      weekday_start: '',
      weekday_stop: '',
      weekend_start: '',
      weekend_stop: '',
      cooling_setpoint: null,
      heating_setpoint: null,
      humidity_setpoint: null,
      co2_setpoint: null,
      sequence_of_operations: '',
    },
    specifications: {
      design_cfm: '',
      fan_bhp: '',
      cooling_capacity: '',
      heating_capacity: '',
      test_date: '',
      measured_cfm: '',
      measured_static: '',
      balancing_contractor: '',
      commissioning_notes: '',
    },
    technical_specs: {
      manufacturer: '',
      model: '',
      serial_number: '',
      capacity: '',
      capacity_unit_id: null,
      power_rating: '',
      efficiency: '',
      install_date: null,
      warranty_expiry: null,
    },
    maintenance_notes: '',
    attached_documents: [],
    brick_uri: '',
    brick_relationships: [],
  });

  const saving = ref(false);
  const loading = ref(false);
  const hasUnsavedChanges = ref(false);
  let autoSaveTimeout = null;

  /**
   * Load documentation from API
   */
  async function loadDocumentation() {
    if (!equipmentId) {return;}

    loading.value = true;
    try {
      const response = await api.get(`/equipment/${equipmentId}/documentation`, {
        params: { type: equipmentType },
      });

      if (response) {
        documentation.value = {
          ...documentation.value,
          ...response,
        };
        hasUnsavedChanges.value = false;
      }
    } catch (error) {
      if (notifyError) {
        notifyError('Failed to load documentation');
      }
    } finally {
      loading.value = false;
    }
  }

  /**
   * Save documentation to API
   */
  async function saveDocumentation() {
    if (!equipmentId) {return;}

    saving.value = true;
    try {
      const response = await api.post(
        `/equipment/${equipmentId}/documentation`,
        documentation.value,
      );

      if (response) {
        if (notifySuccess) {
          notifySuccess('Documentation saved successfully');
        }

        hasUnsavedChanges.value = false;

        if (onSaveSuccess) {
          onSaveSuccess(response);
        }

        return response;
      }
    } catch (error) {
      if (notifyError) {
        notifyError('Failed to save documentation');
      }

      if (onSaveError) {
        onSaveError(error);
      }

      throw error;
    } finally {
      saving.value = false;
    }
  }

  /**
   * Update a field in function_description
   */
  function updateFunctionField(field, value) {
    documentation.value.function_description[field] = value;
    hasUnsavedChanges.value = true;
    scheduleAutoSave();
  }

  /**
   * Update a field in technical_specs
   */
  function updateTechnicalSpec(field, value) {
    documentation.value.technical_specs[field] = value;
    hasUnsavedChanges.value = true;
    scheduleAutoSave();
  }

  /**
   * Update a field in program_schedule
   */
  function updateProgramSchedule(field, value) {
    documentation.value.program_schedule[field] = value;
    hasUnsavedChanges.value = true;
    scheduleAutoSave();
  }

  /**
   * Update a field in specifications
   */
  function updateSpecification(field, value) {
    documentation.value.specifications[field] = value;
    hasUnsavedChanges.value = true;
    scheduleAutoSave();
  }

  /**
   * Update a field in airflow_data
   */
  function updateAirflowField(field, value) {
    documentation.value.airflow_data[field] = value;
    hasUnsavedChanges.value = true;
    scheduleAutoSave();
  }

  /**
   * Update maintenance notes
   */
  function updateMaintenanceNotes(value) {
    documentation.value.maintenance_notes = value;
    hasUnsavedChanges.value = true;
    scheduleAutoSave();
  }

  /**
   * Add a document
   */
  function addDocument(documentData) {
    if (!documentation.value.attached_documents) {
      documentation.value.attached_documents = [];
    }
    documentation.value.attached_documents.push(documentData);
    hasUnsavedChanges.value = true;
    scheduleAutoSave();
  }

  /**
   * Remove a document by index
   */
  function removeDocument(index) {
    documentation.value.attached_documents.splice(index, 1);
    hasUnsavedChanges.value = true;
    scheduleAutoSave();
  }

  /**
   * Add a BRICK relationship
   */
  function addBrickRelationship(relationship) {
    if (!documentation.value.brick_relationships) {
      documentation.value.brick_relationships = [];
    }
    documentation.value.brick_relationships.push(relationship);
    hasUnsavedChanges.value = true;
    scheduleAutoSave();
  }

  /**
   * Remove a BRICK relationship by index
   */
  function removeBrickRelationship(index) {
    documentation.value.brick_relationships.splice(index, 1);
    hasUnsavedChanges.value = true;
    scheduleAutoSave();
  }

  /**
   * Schedule auto-save if enabled
   */
  function scheduleAutoSave() {
    if (!autoSave) {return;}

    // Clear existing timeout
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }

    // Schedule new auto-save
    autoSaveTimeout = setTimeout(() => {
      if (hasUnsavedChanges.value) {
        saveDocumentation();
      }
    }, autoSaveDelay);
  }

  /**
   * Validation: Check if required fields are filled
   */
  const isValid = computed(() => {
    // Add validation logic here if needed
    // For now, always return true
    return true;
  });

  /**
   * Reset form to initial state
   */
  function resetForm() {
    documentation.value = {
      equipment_id: null,
      equipment_type: null,
      airflow_data: {
        supply_flow: null,
        return_flow: null,
        outdoor_air_flow: null,
        static_pressure: null,
        zones_served: [],
        connected_equipment: [],
      },
      function_description: {
        purpose: '',
        operation_modes: '',
        control_strategy: '',
      },
      program_schedule: {
        weekday_start: '',
        weekday_stop: '',
        weekend_start: '',
        weekend_stop: '',
        cooling_setpoint: null,
        heating_setpoint: null,
        humidity_setpoint: null,
        co2_setpoint: null,
        sequence_of_operations: '',
      },
      specifications: {
        design_cfm: '',
        fan_bhp: '',
        cooling_capacity: '',
        heating_capacity: '',
        test_date: '',
        measured_cfm: '',
        measured_static: '',
        balancing_contractor: '',
        commissioning_notes: '',
      },
      technical_specs: {
        manufacturer: '',
        model: '',
        serial_number: '',
        capacity: '',
        capacity_unit_id: null,
        power_rating: '',
        efficiency: '',
        install_date: null,
        warranty_expiry: null,
      },
      maintenance_notes: '',
      attached_documents: [],
      brick_uri: '',
      brick_relationships: [],
    };
    hasUnsavedChanges.value = false;
  }

  return {
    // State
    documentation,
    saving,
    loading,
    hasUnsavedChanges,
    isValid,

    // Methods
    loadDocumentation,
    saveDocumentation,
    updateFunctionField,
    updateTechnicalSpec,
    updateProgramSchedule,
    updateSpecification,
    updateAirflowField,
    updateMaintenanceNotes,
    addDocument,
    removeDocument,
    addBrickRelationship,
    removeBrickRelationship,
    resetForm,
  };
}
